import React from 'react';
import { Card, Tabs, Row, Col, Typography, Tag, Divider } from 'antd';
import PatientHealthOverview from './PatientHealthOverview';
import PreventScore from './PreventScore';
import KidneyDiseaseScore from './KidneyDiseaseScore';
import { calculateGarvanFractureRisk } from '../utils/garvanFractureRisk';
import { AppleOutlined, HeartOutlined, MedicineBoxOutlined, BugOutlined } from '@ant-design/icons';

const { Text } = Typography;

const { TabPane } = Tabs;

const AdvancedHealthCalculators = ({
  patientId,
  loading,
  tableData,
  overallDeviation,
  patientDemographics,
  biologicalAge,
  clinicalFlags,
}) => {
  return (
    <Card
      title="Advanced Health Calculators"
      style={{ marginBottom: 24 }}
    >
      <Tabs defaultActiveKey="1" type="card">
        <TabPane
          tab={<span><AppleOutlined /> Biological Age</span>}
          key="1"
        >
          <PatientHealthOverview
            loading={loading}
            tableData={tableData}
            overallDeviation={overallDeviation}
            patientDemographics={patientDemographics}
            biologicalAge={biologicalAge}
            patientId={patientId}
          />
        </TabPane>
        <TabPane
          tab={<span><HeartOutlined /> Cardiovascular Risk (PREVENT)</span>}
          key="2"
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
              <PreventScore
                tableData={tableData}
                patientDemographics={patientDemographics}
              />
            </Col>
          </Row>
        </TabPane>
        <TabPane
          tab={<span><MedicineBoxOutlined /> Fracture Risk (Garvan) ✅</span>}
          key="3"
        >
          {(() => {
            const getParameterValue = (paramKey) => {
              if (!tableData || !Array.isArray(tableData)) return null;
              // tableData is an array of category groups, each with a parameters array
              for (const categoryGroup of tableData) {
                const param = (categoryGroup.parameters || []).find(p => p.key === paramKey);
                if (param && param.currentValue !== null && param.currentValue !== undefined) {
                  const numValue = parseFloat(param.currentValue);
                  return isNaN(numValue) ? null : numValue;
                }
              }
              return null;
            };

            const currentAge = patientDemographics?.birthDate
              ? Math.floor((new Date() - new Date(patientDemographics.birthDate)) / (365.25 * 24 * 60 * 60 * 1000))
              : null;

            // Try multiple possible weight parameter keys
            let weight = getParameterValue('vitals_weight');
            if (!weight) weight = getParameterValue('biometrics_weight');
            if (!weight) weight = getParameterValue('weight');

            // Get Garvan risk factors from patient demographics (set in Clinical Review)
            const priorFracturesCount = patientDemographics?.priorFracturesCount || 0;
            const fallsCount = patientDemographics?.fallsCount || 0;

            const garvanParams = {
              age: currentAge,
              sex: patientDemographics?.sex?.toLowerCase(),
              weight: weight,
              priorFracturesCount: priorFracturesCount,
              fallsCount: fallsCount,
              fractureType: 'any'
            };

            const garvanResult = calculateGarvanFractureRisk(garvanParams);

            return (
              <div>
                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#e6f7ff', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                  <Text style={{ fontSize: '13px', color: '#0050b3' }}>
                    <strong>✅ Validated Calculator:</strong> Garvan uses published coefficients from peer-reviewed research (Nguyen et al. 2007, 2008).
                    All calculations are clinically validated and produce accurate risk estimates.
                  </Text>
                </div>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <div style={{ padding: '12px', backgroundColor: '#f6ffed', borderRadius: '6px', border: '1px solid #b7eb8f' }}>
                      <Text strong style={{ color: '#52c41a' }}>Patient Demographics:</Text>
                      <ul style={{ margin: '8px 0 0 20px', fontSize: '12px' }}>
                        <li>Age: {garvanParams.age || 'N/A'} years</li>
                        <li>Sex: {garvanParams.sex || 'N/A'}</li>
                        <li>Weight: {garvanParams.weight || 'N/A'} kg</li>
                      </ul>
                      <Text strong style={{ color: '#52c41a', marginTop: '8px', display: 'block' }}>Risk Factors:</Text>
                      <ul style={{ margin: '4px 0 0 20px', fontSize: '12px' }}>
                        <li>Prior Fractures: {garvanParams.priorFracturesCount}</li>
                        <li>Falls (past year): {garvanParams.fallsCount}</li>
                      </ul>
                    </div>
                  </Col>
                  <Col xs={24} md={12}>
                    <div style={{ padding: '12px', backgroundColor: garvanResult.error ? '#fff2e8' : '#f0f9ff', borderRadius: '6px', border: `1px solid ${garvanResult.error ? '#ffbb96' : '#91d5ff'}` }}>
                      <Text strong style={{ color: garvanResult.error ? '#d46b08' : '#1890ff' }}>Garvan Results:</Text>
                      {garvanResult.error ? (
                        <div style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#d46b08' }}>
                          <div><strong>Cannot calculate fracture risk</strong></div>
                          {garvanResult.missing && garvanResult.missing.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                              <strong>Missing:</strong> {garvanResult.missing.join(', ')}
                            </div>
                          )}
                          <div style={{ marginTop: '8px', fontSize: '11px', backgroundColor: '#fff7e6', padding: '8px', borderRadius: '4px' }}>
                            <strong>💡 To calculate fracture risk, please add:</strong>
                            <ul style={{ margin: '4px 0 0 16px' }}>
                              {!garvanParams.age && <li>Patient birth date (in Demographics)</li>}
                              {!garvanParams.sex && <li>Patient sex (in Demographics)</li>}
                              {!garvanParams.weight && <li>Patient weight (in Flowsheet → Add Data → Vitals)</li>}
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <div style={{ margin: '8px 0 0 0' }}>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: garvanResult.color, marginBottom: '4px' }}>
                            5-Year Risk: {garvanResult.risk5Year}%
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: garvanResult.color, marginBottom: '4px' }}>
                            10-Year Risk: {garvanResult.risk10Year}%
                          </div>
                          <Tag color={garvanResult.color} style={{ marginTop: '4px' }}>
                            {garvanResult.category}
                          </Tag>
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {garvanResult.description}
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                {!garvanResult.error && garvanResult.calculationDetails && (
                  <div style={{ marginTop: '16px' }}>
                    <Divider orientation="left">Calculation Details</Divider>
                    <div style={{ backgroundColor: '#fafafa', padding: '12px', borderRadius: '6px', border: '1px solid #d9d9d9' }}>
                      <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                        <div><strong>Model Used:</strong> {garvanResult.modelUsed}</div>
                        <div><strong>Age:</strong> {garvanResult.calculationDetails.age} years</div>
                        <div><strong>Sex:</strong> {garvanResult.calculationDetails.sex}</div>
                        {garvanResult.calculationDetails.weight && (
                          <div><strong>Weight:</strong> {garvanResult.calculationDetails.weight} kg</div>
                        )}
                        {garvanResult.calculationDetails.bmd && (
                          <div><strong>BMD:</strong> {garvanResult.calculationDetails.bmd} g/cm²</div>
                        )}
                        <div><strong>Prior Fractures:</strong> {garvanResult.calculationDetails.priorFractures}</div>
                        <div><strong>Falls (past year):</strong> {garvanResult.calculationDetails.falls}</div>
                      </div>
                    </div>
                    {garvanResult.warnings && garvanResult.warnings.length > 0 && (
                      <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fffbe6', borderRadius: '4px', border: '1px solid #ffe58f' }}>
                        <Text style={{ fontSize: '11px', color: '#d48806' }}>
                          ⚠️ {garvanResult.warnings.join('; ')}
                        </Text>
                      </div>
                    )}
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                      <div><strong>Source:</strong> {garvanResult.source}</div>
                      <div><strong>Methodology:</strong> {garvanResult.methodology}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </TabPane>
        <TabPane
          tab={<span><BugOutlined /> GFR Decline Risk</span>}
          key="4"
        >
          <KidneyDiseaseScore
            patientId={patientId}
          />
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default AdvancedHealthCalculators;
